import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Modal, QRCode, Segmented, Space, Spin, Empty } from 'antd';
import { AndroidOutlined, AppleOutlined, QrcodeOutlined } from '@ant-design/icons';
import axios from '../../config/axiosInstance';
import { useTheme } from "../../context/ThemeContext";

const { Title, Text } = Typography;

const LeadCaptureCard = () => {
    const { darkTheme } = useTheme();

    const [qrVisible, setQrVisible] = useState(false);
    const [qrValue, setQrValue] = useState('');
    const [qrIcon, setQrIcon] = useState('');
    const [renderType, setRenderType] = useState('canvas');

    const [loading, setLoading] = useState(true);
    const [appData, setAppData] = useState(null);

    useEffect(() => {
        const fetchLeadCaptureLatest = async () => {
            try {
                const response = await axios.get('/apps/leadcapture/latest');
                setAppData(response.data);
            } catch (error) {
                console.error('Error loading LeadCapture latest version:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLeadCaptureLatest();
    }, []);

    const showQRCode = (link, platform) => {
        setQrValue(link);
        setQrIcon(
            platform === 'android'
                ? 'https://cdn-icons-png.flaticon.com/512/888/888857.png'
                : 'https://cdn-icons-png.flaticon.com/512/831/831276.png'
        );
        setQrVisible(true);
    };

    const doDownload = (url, fileName) => {
        const a = document.createElement('a');
        a.download = fileName;
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const downloadCanvasQRCode = () => {
        const canvas = document.querySelector('#myqrcode canvas');
        if (canvas) {
            const url = canvas.toDataURL();
            doDownload(url, 'LeadCaptureQR.png');
        }
    };

    const downloadSvgQRCode = () => {
        const svg = document.querySelector('#myqrcode svg');
        if (svg) {
            const svgData = new XMLSerializer().serializeToString(svg);
            const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            doDownload(url, 'LeadCaptureQR.svg');
        }
    };

    const platforms = [
        appData?.android
            ? {
                type: 'android',
                name: 'Android',
                version: appData.android.version,
                link: appData.android.link,
                icon: (
                    <AndroidOutlined
                        style={{
                            fontSize: 42,
                            color: darkTheme ? '#A4C639' : '#3DDC84',
                        }}
                    />
                )
            }
            : null,
        appData?.ios
            ? {
                type: 'ios',
                name: 'iOS',
                version: appData.ios.version,
                link: 'itms-services://?action=download-manifest&url=https://regasus.de/apps/leadcapture/manifest.plist',
                /*link: appData.ios.link || appData.ios.ipaLink, */
                icon: (
                    <AppleOutlined
                        style={{
                            fontSize: 42,
                            color: darkTheme ? '#FFFFFF' : '#000000',
                        }}
                    />
                )
            }
            : null
    ].filter(Boolean);

    return (
        <>
            <Card
                style={{
                    backgroundColor: darkTheme ? '#141414' : '#ffffff',
                    border: darkTheme ? '1px solid #303030' : '1px solid #f0f0f0'
                }}
            >
                <Title level={4} style={{ marginTop: 0, marginBottom: 20 }}>
                    LeadCapture
                </Title>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                        <Spin />
                    </div>
                ) : !platforms.length ? (
                    <Empty description="No versions found" />
                ) : (
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {platforms.map((platform) => (
                            <Card
                                key={platform.type}
                                size="small"
                                style={{
                                    width: 180,
                                    textAlign: 'center',
                                    backgroundColor: darkTheme ? '#1f1f1f' : '#ffffff',
                                    border: darkTheme ? '1px solid #303030' : '1px solid #f0f0f0',
                                    borderRadius: '10px',
                                    boxShadow: darkTheme
                                        ? '0 2px 8px rgba(255, 255, 255, 0.08)'
                                        : '0 2px 8px rgba(0, 0, 0, 0.08)'
                                }}
                            >
                                {platform.icon}
                                <Title level={5}>{platform.name}</Title>
                                <Text>Version: {platform.version || 'unknown'}</Text>

                                <div style={{ marginTop: 16 }}>
                                    <Button
                                        type="primary"
                                        icon={<QrcodeOutlined />}
                                        onClick={() => showQRCode(platform.link, platform.type)}
                                        disabled={!platform.link}
                                    >
                                        Show QR
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </Card>

            <Modal
                open={qrVisible}
                title="QR Code"
                footer={null}
                onCancel={() => setQrVisible(false)}
                centered
                width={280}
                modalRender={(node) => (
                    <div style={{ padding: 0, margin: 0, textAlign: 'center' }}>
                        {node}
                    </div>
                )}
                style={{ padding: 0 }}
                bodyStyle={{ padding: 5, margin: 0, display: 'flex', justifyContent: 'center' }}
            >
                <Space id="myqrcode" direction="vertical" align="center">
                    <Segmented
                        options={[
                            { label: 'PNG', value: 'canvas' },
                            { label: 'SVG', value: 'svg' }
                        ]}
                        value={renderType}
                        onChange={setRenderType}
                    />

                    <QRCode
                        type={renderType}
                        errorLevel="H"
                        value={qrValue}
                        icon={qrIcon}
                        size={200}
                        style={{ marginBottom: 16 }}
                    />

                    <Button
                        type="primary"
                        onClick={renderType === 'canvas' ? downloadCanvasQRCode : downloadSvgQRCode}
                    >
                        Download QR
                    </Button>
                </Space>
            </Modal>
        </>
    );
};

export default LeadCaptureCard;